import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import moment from 'moment/moment';
import * as constants from 'utils/constants';

import { IdentificationPassport as ApiPassport } from '../../../../../api-client/models/identification/common/IdentificationPassport';
import { useEffectOnMount, useEffectOnUpdate, useProfileData } from '../../../../../hooks';
import { Errors as ValidationErrors, numbersOnly } from '../../../../../utils';
import { ymAsync } from '../../../../../utils/functions/ymWrapper';
import { CommonInput, Heading, InputErrors, SecondaryHeading } from '../../../../common';
import { HeadingType } from '../../../../common/typography/headings/Heading/Heading';
import IdentificationContext from '../../../../context/IdentificationContext';
import {
  FirstScanUpload,
  IdentificationDateSelect,
  SecondScanUpload,
} from '../../../../pages/identification';
import ThirdScanUpload from '../../../../pages/identification/ThirdScanUpload';
import { IdentificationBlock } from '../index';

import './IdentificationPassport.scoped.scss';

export type IdentificationPassportErrors = {
  passportSeries?: ValidationErrors;
  passportNumber?: ValidationErrors;
  passportIssuedDate?: ValidationErrors;
  passportIssuedBy?: ValidationErrors;
  passportDepartmentCode?: ValidationErrors;
  firstPassportScans?: ValidationErrors;
  secondPassportScans?: ValidationErrors;
  thirdPassportScans?: ValidationErrors;
};

interface Props {
  onValidated: (value: boolean) => void;
  passport?: ApiPassport;
}

const IdentificationPassport: FC<Props> = ({ onValidated, passport }) => {
  const [errors, setErrors] = useState<IdentificationPassportErrors>({});
  const [scansWereReset, setScansWereReset] = useState(false);
  const context: any = useContext(IdentificationContext);
  const profileData = useProfileData();
  const isVerified = useMemo(() => profileData.lenderVerified, [profileData.lenderVerified]);
  useEffectOnMount(() => {
    if (passport) {
      context.setPassportSeries(passport.series);
      context.setPassportNumber(passport.number);
      context.setPassportIssuedDate(moment(passport.issuedDate).toDate());
      context.setPassportIssuedBy(passport.issuedBy);
      context.setPassportDepartmentCode(passport.departmentCode);
      context.setFirstPassportScans(passport.firstPassportScans || []);
      context.setSecondPassportScans(passport.secondPassportScans || []);
      context.setThirdPassportScans(passport.thirdPassportScans || []);
    }
  });
  const validateBlock = (): boolean => {
    const errors: IdentificationPassportErrors = {};

    if (!context.passportSeries) {
      errors.passportSeries = ['Серия паспорта обязательна для заполнения'];
    } else if (!numbersOnly(context.passportSeries)) {
      errors.passportSeries = ['Серия паспорта должна состоять из цифр'];
    } else if (context.passportSeries.length !== constants.PASSPORT_SERIES_LENGTH) {
      errors.passportSeries = [`Длина серии паспорта - ${constants.PASSPORT_SERIES_LENGTH} цифры`];
    }

    if (!context.passportNumber) {
      errors.passportNumber = ['Номер паспорта обязателен для заполнения'];
    } else if (!numbersOnly(context.passportNumber)) {
      errors.passportNumber = ['Номер паспорта должен состоять из цифр'];
    } else if (context.passportNumber.length !== constants.PASSPORT_NUMBER_LENGTH) {
      errors.passportNumber = [`Длина номера паспорта - ${constants.PASSPORT_NUMBER_LENGTH} цифр`];
    }

    if (!context.passportIssuedBy) {
      errors.passportIssuedBy = ['Данное поле обязательно к заполнению'];
    }

    if (!context.passportDepartmentCode) {
      errors.passportDepartmentCode = ['Код подразделения обязателен к заполнению'];
    } else if (!/^\d{3}-\d{3}$/.test(context.passportDepartmentCode)) {
      errors.passportDepartmentCode = ['Формат кода должен быть вида: ***-***'];
    }

    if (!context.firstPassportScans.length) {
      errors.firstPassportScans = ['Приложите файл со сканом первого разворота с фотографией'];
    }

    if (!context.secondPassportScans.length) {
      errors.secondPassportScans = ['Приложите файл со сканом разворота с регистрацией'];
    }

    if (isVerified && !context.thirdPassportScans.length) {
      errors.thirdPassportScans = [
        'Приложите файл со сканом разворота с информацией о ранее выданных паспортах',
      ];
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };
  useEffectOnUpdate(() => {
    const isValid = validateBlock();
    onValidated(isValid);

    if (!isVerified || !passport) return;

    const isDataChanged =
      context.passportSeries !== passport.series ||
      context.passportNumber !== passport.number ||
      context.passportIssuedBy !== passport.issuedBy ||
      context.passportDepartmentCode !== passport.departmentCode ||
      new Date(context.passportIssuedDate).toISOString().split('T')[0] !==
        new Date(passport.issuedDate).toISOString().split('T')[0];

    if (isDataChanged && !scansWereReset) {
      context.setFirstPassportScans([]);
      context.setSecondPassportScans([]);
      context.setThirdPassportScans([]);
      setScansWereReset(true);
    }
  }, [
    context.passportSeries,
    context.passportNumber,
    context.passportIssuedDate,
    context.passportIssuedBy,
    context.passportDepartmentCode,
    context.firstPassportScans,
    context.secondPassportScans,
    context.thirdPassportScans,
    isVerified,
    passport,
    scansWereReset,
  ]);

  useEffect(() => {
    if (passport) {
      setScansWereReset(false);
    }
  }, [passport]);

  const ymSelectPassportDate = (value: Date) => {
    context.setPassportIssuedDate(value);
    ymAsync('verification-passport-date-input');
  };

  return (
    <IdentificationBlock>
      <SecondaryHeading>
        <Heading headingType={HeadingType.H2} className="identification__passport-heading">
          Паспортные данные
        </Heading>
      </SecondaryHeading>
      <div className="identification__passport-block">
        <div className="identification__passport-series">
          <CommonInput
            value={context.passportSeries}
            onChange={(value) => context.setPassportSeries(value)}
            onBlur={() => {
              ymAsync('verification-passport-series-input');
            }}
            title="Серия паспорта"
            id="passportSeries"
            required
          />
          {errors.passportSeries && <InputErrors errors={errors.passportSeries} />}
        </div>
        <div className="identification__passport-number">
          <CommonInput
            value={context.passportNumber}
            onChange={(value) => context.setPassportNumber(value)}
            onBlur={() => {
              ymAsync('verification-passport-number-input');
            }}
            title="Номер паспорта"
            id="passportNumber"
            required
          />
          {errors.passportNumber && <InputErrors errors={errors.passportNumber} />}
        </div>
        <div className="identification__passport-issued-by">
          <CommonInput
            value={context.passportIssuedBy}
            onChange={(value) => context.setPassportIssuedBy(value)}
            onBlur={() => {
              ymAsync('verification-place-issue-passport-input');
            }}
            title="Кем выдан"
            id="passportIssuedBy"
            required
          />
          {errors.passportIssuedBy && <InputErrors errors={errors.passportIssuedBy} />}
        </div>
        <div className="identification__passport-issued-date">
          <IdentificationDateSelect
            header="Дата выдачи"
            value={context.passportIssuedDate}
            onSelected={ymSelectPassportDate}
          />
          {errors.passportIssuedDate && <InputErrors errors={errors.passportIssuedDate} />}
        </div>
        <div className="identification__passport-code">
          <CommonInput
            value={context.passportDepartmentCode}
            onChange={(value) => context.setPassportDepartmentCode(value)}
            onBlur={() => {
              ymAsync('verification-code-department-passport-input');
            }}
            title="Код подразделения"
            id="passportDivisionCode"
            required
          />
          {errors.passportDepartmentCode && <InputErrors errors={errors.passportDepartmentCode} />}
        </div>
      </div>
      <div className="identification__passport-files">
        <div className="identification__file-name">Загрузите изображение паспорта</div>
      </div>
      <div className={`identification__passport-files-grid ${isVerified ? 'verified' : ''}`}>
        <div>
          <FirstScanUpload />
          {errors.firstPassportScans && <InputErrors errors={errors.firstPassportScans} />}
        </div>
        <div>
          <SecondScanUpload />
          {errors.secondPassportScans && <InputErrors errors={errors.secondPassportScans} />}
        </div>
        {isVerified && (
          <div>
            <ThirdScanUpload />
            {errors.thirdPassportScans && <InputErrors errors={errors.thirdPassportScans} />}
          </div>
        )}
        <div className="identification-files__warning">
          Загружайте изображение в формате jpg, png или pdf. Файл не должен превышать{' '}
          <span className="identification-files__warning-size"> 4 Мб.</span>
        </div>
      </div>
    </IdentificationBlock>
  );
};

export default IdentificationPassport;
